import dayjs from 'dayjs'

import { PAGE_DATA as FUNNEL_PAGE_DATA } from '~/constants/funnel/health-insurance'
import { getUcaMinDate } from '~/utils/helpers'
import {
  COMPARE_STEP,
  COMPARE_STEP_NUMBER,
  PERSONAL_DETAILS_STEP,
  PERSONAL_DETAILS_STEP_NUMBER,
  PARTNER_DETAILS_STEP,
  PARTNER_DETAILS_STEP_NUMBER,
  CHILDREN_DETAILS_STEP,
  CHILDREN_DETAILS_STEP_NUMBER,
  REBATE_STEP,
  REBATE_STEP_NUMBER,
  LHC_STEP,
  LHC_STEP_NUMBER,
  CHOOSE_EXTRAS_STEP,
  CHOOSE_EXTRAS_STEP_NUMBER,
  MEDICARE_STEP,
  MEDICARE_STEP_NUMBER,
  ACKNOWLEDGEMENT_STEP,
  ACKNOWLEDGEMENT_STEP_NUMBER,
  REVIEW_STEP,
  REVIEW_STEP_NUMBER,
  PAYMENT_STEP,
  PAYMENT_STEP_NUMBER
} from '#shared/health-application-steps'
import { ACCOUNT_NAME_MAX_LENGTH } from '~/constants/payment.constants'

const MARKETING_CONSENT_FUND_LABELS = {
  hbf: 'HBF',
  'see-u': 'See-U'
}

export function getInsurerMarketingConsentOptions (fundName) {
  const displayName = MARKETING_CONSENT_FUND_LABELS[fundName?.toLowerCase()] ?? 'your fund'

  return [
    {
      value: 'Yes',
      label: `By selecting this box, you agree to receive marketing updates from ${displayName}, including emails, SMS, phone calls, and mail about ${displayName}'s products and offers. You can opt out at any time.`,
      name: 'insurer-marketing-consent-yes'
    }
  ]
}

// Maps the /product/choosable-extras API shape ({ id, display_name }) into
// V2AtomsSelection option objects. The option value is the choosable_extra `id`
// prefixed with `extra-`: V2AtomsSelection's ticker compares the model against
// `optionIndex + 1` (Selection/index.vue), so a bare numeric id ticks whichever
// option sits at that index. The store strips the prefix and submits integers --
// the backend (HealthApplicationManager -> ChoosableExtrasApplicationManager
// -> ChoosableExtraRepository::findValidSelectionsForProduct) validates and stores
// choosable_extras as an array of IDs, not display names.
// display_name is used only for the on-screen label and for the tooltip lookup
// against extrasCoverList. Extras with no matching tooltip entry render without one.
export function getChoosableExtrasOptions (extras) {
  return (extras ?? []).map(extra => {
    const infoEntry = FUNNEL_PAGE_DATA.extrasCoverList.find(item => item.value === extra.display_name)

    return {
      value: `extra-${extra.id}`,
      label: extra.display_name,
      ...(infoEntry?.info ? { info: infoEntry.info } : {})
    }
  })
}

export const PAGE_DATA = {
  title: ['Mr', 'Ms', 'Mrs', 'Miss', 'Dr'],
  gender: ['Male', 'Female'],
  incomes: {
    single: [
      {
        label: '$105,000 or less (Base tier)',
        desc: 'A 24.118% (Base tier) government rebate will be applied.',
        value: 'nil',
        tierLabel: 'Base Tier',
        rebate: 24.118,
        idSuffix: 'annualincome-nil',
        income: '$105,000 or less'
      },
      {
        label: '$105,001 - $123,000 (Tier 1)',
        desc: 'A 16.079% (Tier 1) government rebate will be applied.',
        value: 'low',
        rebate: 16.079,
        idSuffix: 'annualincome-low',
        income: '$105,001 - $123,000',
        tierLabel: 'Tier 1'
      },
      {
        label: '$123,001 - $164,000 (Tier 2)',
        desc: 'A 8.038% (Tier 2) government rebate will be applied.',
        value: 'medium',
        rebate: 8.038,
        idSuffix: 'annualincome-medium',
        income: '$123,001 - $164,000',
        tierLabel: 'Tier 2'
      },
      {
        label: '$164,001 or more (no rebate) (Tier 3)',
        desc: 'No government rebate (Tier 3) will be applied.',
        value: 'high',
        rebate: '0.00',
        idSuffix: 'annualincome-high',
        income: '$164,001 or more',
        tierLabel: 'Tier 3'
      },
      {
        label: 'Do not apply rebate',
        desc: 'We will not apply Government rebate.',
        value: 'none',
        rebate: '0.00',
        idSuffix: 'none',
        income: '$0',
        tierLabel: ''
      }
    ],
    couple: [
      {
        label: '$210,000 or less (Base tier)',
        desc: 'A 24.118% (Base tier) government rebate will be applied.',
        value: 'nil',
        rebate: 24.118,
        idSuffix: 'annualincome-nil',
        income: '$210,000 or less',
        tierLabel: 'Base Tier'
      },
      {
        label: '$210,001 - $246,000 (Tier 1)',
        desc: 'A 16.079% (Tier 1) government rebate will be applied.',
        value: 'low',
        rebate: 16.079,
        idSuffix: 'annualincome-low',
        income: '$210,001 - $246,000',
        tierLabel: 'Tier 1'
      },
      {
        label: '$246,001 - $328,000 (Tier 2)',
        desc: 'A 8.038% (Tier 2) government rebate will be applied.',
        value: 'medium',
        tierLabel: 'Tier 2',
        rebate: 8.038,
        idSuffix: 'annualincome-medium',
        income: '$246,001 - $328,000'
      },
      {
        label: '$328,001 or more (no rebate) (Tier 3)',
        desc: 'No government rebate (Tier 3) will be applied.',
        value: 'high',
        rebate: 0,
        idSuffix: 'annualincome-high',
        income: '$328,001 or more',
        tierLabel: 'Tier 3'
      },
      {
        label: 'Do not apply rebate',
        desc: 'We will not apply Government rebate.',
        value: 'none',
        rebate: 0,
        idSuffix: 'annualincome-none',
        income: '$0',
        tierLabel: ''
      }
    ]
  }
}

// Step slugs and numbers live in shared/ so nuxt.config.ts and the Nitro
// plugins (csp-nonce, strip-optimizely-payment-hints) read the same values as
// the app. Re-exported so existing app imports keep working.
export {
  COMPARE_STEP,
  COMPARE_STEP_NUMBER,
  PERSONAL_DETAILS_STEP,
  PERSONAL_DETAILS_STEP_NUMBER,
  PARTNER_DETAILS_STEP,
  PARTNER_DETAILS_STEP_NUMBER,
  CHILDREN_DETAILS_STEP,
  CHILDREN_DETAILS_STEP_NUMBER,
  REBATE_STEP,
  REBATE_STEP_NUMBER,
  LHC_STEP,
  LHC_STEP_NUMBER,
  CHOOSE_EXTRAS_STEP,
  CHOOSE_EXTRAS_STEP_NUMBER,
  MEDICARE_STEP,
  MEDICARE_STEP_NUMBER,
  ACKNOWLEDGEMENT_STEP,
  ACKNOWLEDGEMENT_STEP_NUMBER,
  REVIEW_STEP,
  REVIEW_STEP_NUMBER,
  PAYMENT_STEP,
  PAYMENT_STEP_NUMBER
}

export const ERROR_MAP = {
  title: 'Please select title',
  email: 'Please enter valid email address',
  mobile: 'Please enter a valid number',
  phone: 'Please enter a valid number.',
  birthdate: 'Please enter your date of birth',
  first_name: 'Please enter valid First Name',
  last_name: 'Please enter valid Last Name',
  gender: 'Please select your gender',
  preferred_contact_method: 'Choose your preferred contact method',
  street_address: 'We couldn\'t find this address. Please check the details and try again.',
  post_street_address: 'We couldn\'t find this postal address. Please check the details and try again.',

  partner_title: 'Please select title',
  partner_first_name: 'First name is required',
  partner_last_name: 'Last name is required',
  partner_birthdate: 'Please enter the Date of Birth',
  partner_gender: 'Please select the gender',
  partner_mobile: 'Please enter a valid mobile number',
  partner_email: 'Please enter valid email address',

  children: 'Please complete',

  lhc_age_primary: 'Please enter age',
  lhc_age_partner: 'Please enter age',
  insurer: 'Please select your previous fund',
  insurer_other: 'Please enter your previous fund name',
  partner_insurer: 'Please select your partner\'s previous fund',
  partner_insurer_other: 'Please enter your partner\'s previous fund name',

  govt_declaration: 'Please select an option',
  income: 'Please select an option',
  choosable_extras: 'Please select at least one extra',
  medicare_card_number: 'Input Medicare no',
  medicare_expired: 'Invalid expiry date',
  medicare_color: 'Please select a Medicare card colour',
  medicare_first_name: 'Please enter the first name as shown on the card',
  medicare_last_name: 'Please enter the last name as shown on the card',
  has_medicare: 'Please select an option',
  medicare_name: 'Please enter the name as shown on the card',

  bsb: 'Input BSB',
  bank_no: 'Input account number',
  bank_acc_name: 'Please input account name',
  bank_name: 'Please input financial institution',
  payment_method: 'Please select payment',

  policy_start_date: 'Invalid policy start date',
  direct_credit_account_name: 'Please input account name',
  credit_bsb: 'Please input valid BSB',
  credit_bank_no: 'Please input valid account number',
  card_type: 'Please select your credit card type',
  card_exp_date: 'Invalid expiry date',
  cardholder_name: 'Please input cardholder\'s name',
  card_number: 'Input valid credit card',
  hbf_credit_card: 'Input valid credit card'
}

export const CHILDREN_FORM = {
  childFirstName: {
    question: 'First name',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'child_first_name',
          placeholder: 'Your child\'s first name',
          label: '',
          theme: 'light',
          'data-testid': 'child-first-name-input'
        }
      }
    ]
  },

  childMiddleName: {
    question: 'Middle name (optional)',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'child_middle_name',
          placeholder: 'Your child\'s middle name',
          label: '',
          theme: 'light',
          'data-testid': 'child-middle-name-input'
        }
      }
    ]
  },

  childLastName: {
    question: 'Last name',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'child_last_name',
          placeholder: 'Your child\'s last name',
          label: '',
          theme: 'light',
          'data-testid': 'child-last-name-input'
        }
      }
    ]
  },

  childBirthDate: {
    question: 'Date of Birth',
    components: [
      {
        component: 'MoleculesBirthdate',
        hidden: true,
        props: {
          label: 'Date of birth',
          placeholder: 'Enter your child\'s date of birth (dd/mm/yyyy)',
          validAge: [1, 120],
          disableDatepicker: true,
          theme: 'light',
          hideSuccessIcon: true,
          'data-testid': 'child-dob-input'
        }
      }
    ]
  },
  
  childGender: {
    question: 'Gender',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'child_gender',
          type: 'radio',
          ticker: 'circle',
          options: ['Male', 'Female'],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'child-gender-radio'
        }
      }
    ]
  },

  isChildStudent: {
    question: 'Is this dependant a full-time student?',
    hidden: true,
    desc: 'Some insurers may charge an additional fee for non-students or students over 21 but under 31 years of age. Extended family premiums may apply.',
    components: [
      {
        component: 'MoleculesSelection',
        hidden: true,
        props: {
          name: 'is_child_student',
          type: 'radio',
          ticker: 'circle',
          cols: 2,
          mobileCols: 2,
          options: ['Yes', 'No'],
          'data-testid': 'is-child-student-radio'
        }
      }
    ]
  },

  uniName: {
    question: 'University name (optional)',
    hidden: true,
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'uni_name',
          placeholder: 'University name',
          label: '',
          theme: 'light',
          'data-testid': 'child-uni-name-input'
        }
      }
    ]
  },

  uniCompletionDate: {
    question: 'Date of course completion (optional)',
    hidden: true,
    components: [
      {
        component: 'MoleculesBirthdate',
        props: {
          name: 'uni_completion_date',
          label: 'Completion date',
          placeholder: 'Completion date (dd/mm/yyyy)',
          disableDatepicker: true,
          theme: 'light',
          'data-testid': 'child-uni-completion-input'
        }
      }
    ]
  },

  isChildCovered: {
    question: 'Has this dependant held private hospital cover before?',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'is_child_covered',
          type: 'radio',
          ticker: 'circle',
          options: ['Yes', 'No'],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'child-currently-insured-radio'
        }
      }
    ]
  },

  childCurrentFund: {
    question: 'Current or last health fund',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'child_current_fund',
          label: 'Health Fund',
          theme: 'light',
          placeholder: 'Select health fund',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          searchable: true,
          dataSrc: 'https://asset.compareclub.com.au/json/health-funnel-funds.json',
          'data-testid': 'child-current-fund-dropdown'
        }
      }
    ]
  }
}

export const CHILDREN_ERROR_MAP = {
  child_title: 'Please select your child\'s title',
  child_gender: 'Please select your child\'s gender',
  child_first_name: 'Please enter your child\'s first name',
  child_last_name: 'Please enter your child\'s last name',
  child_birth_date: 'Birthdate must be in the format DD/MM/YYYY',
  is_child_student: 'Please select one',
  uni_completion_date: 'Please enter valid date of university completion date',
  is_child_covered: 'Please select an option',
  child_current_fund: 'Please select a health fund'
}

/**
 * HI-1550: the earliest policy start date the customer may select.
 *
 * Unsolicited (UCA) sales must start strictly more than 10 working days from the submission
 * date - see getUcaMinDate(). Everyone else keeps the existing "tomorrow" floor. Shared by
 * disableDate, defaultValue and the helper notice so the three can never disagree.
 */
export const getPolicyStartMinDate = () => {
  const applicationStore = useHealthApplicationStore()

  if (applicationStore?.applicationData?.unsolicited) {
    return getUcaMinDate()
  }

  return dayjs().startOf('day').add(1, 'day')
}

/**
 * QA 2026-09-18: the picker skips ahead to a later month for unsolicited sales, so say why.
 * Returns an empty string for solicited sales, where tomorrow is already selectable.
 */
const getPolicyStartEarliestDateNotice = () => {
  const applicationStore = useHealthApplicationStore()

  if (!applicationStore?.applicationData?.unsolicited) {
    return ''
  }

  return `<br /><span class="text-sm font-bold">Earliest available date: ${getPolicyStartMinDate().format('D MMM YYYY')}</span>`
}

export const FORM_QUESTIONS = {
  title: {
    question: 'Title',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'title',
          wrapperClass: 'hidden lg:block',
          label: 'Title',
          theme: 'light',
          placeholder: 'Title',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          dataArr: PAGE_DATA.title,
          'data-testid': 'title-dropdown'
        }
      },
      {
        component: 'MoleculesSelection',
        props: {
          name: 'title',
          class: 'lg:hidden',
          type: 'radio',
          ticker: 'circle',
          options: PAGE_DATA.title,
          cols: 2,
          mobileCols: 2
        }
      }
    ]
  },
  firstName: {
    question: 'First Name',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'first_name',
          placeholder: 'Enter first name',
          label: '',
          theme: 'light',
          'data-testid': 'first-name-input'
        }
      }
    ]
  },
  middleName: {
    question: 'Middle name (optional)',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'middle_name',
          placeholder: 'Enter middle name',
          label: '',
          theme: 'light',
          'data-testid': 'middle-name-input'
        }
      }
    ]
  },
  lastName: {
    question: 'Last name',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'last_name',
          placeholder: 'Enter last name',
          label: '',
          theme: 'light',
          'data-testid': 'last-name-input'
        }
      }
    ]
  },
  birthdate: {
    question: 'Date of Birth',
    components: [
      {
        component: 'MoleculesBirthdate',
        props: {
          label: 'Date of birth',
          placeholder: 'Enter your date of birth (dd/mm/yyyy)',
          validAge: [18,100],
          disableDatepicker: true,
          theme: 'light',
          'data-testid': 'birthdate-input'
        }
      }
    ]
  },
  gender: {
    question: 'Gender',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'gender',
          type: 'radio',
          ticker: 'circle',
          options: PAGE_DATA.gender,
          cols: 2,
          mobileCols: 2,
          'data-testid': 'gender-radio'
        }
      }
    ]
  },
  mobile: {
    question: 'Mobile Phone',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'mobile',
          placeholder: 'Your mobile number',
          label: '',
          theme: 'light',
          validator: 'mobileNumberValidator',
          'data-testid': 'mobile-input'
        }
      }
    ]
  },
  phone: {
    question: 'Landline <span class="font-normal">(optional)</span>',
    wrapperClass: 'mb-3',
    isToggle: true,
    toggleLabel: 'Add landline number',
    toggleIcon: 'V2IconsPlus',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'phone',
          placeholder: 'Your landline number',
          label: '',
          theme: 'light',
          modelType: 'number',
          validator: 'landlineNumberValidator',
          'data-testid': 'landline-input'
        }
      }
    ]
  },
  email: {
    question: 'Email',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'email',
          placeholder: 'Enter your email',
          label: '',
          theme: 'light',
          validator: 'emailValidator',
          'data-testid': 'email-input'
        }
      }
    ]
  },
  streetAddress: {
    question: 'Residential address',
    wrapperClass: 'mt-6',
    components: [
      {
        component: 'MoleculesFormAddress',
        props: {
          name: 'street_address',
          placeholder: 'Start typing your address',
          defaultPostcode: '2000',
          defaultState: 'NSW',
          defaultSuburb: '',
          stickyAddress: true,
          showAddresslineError: false,
          'data-testid': 'street-address-input',
          showPencilIcon: true
        }
      }
    ]
  },
  postalIsSameAsResidential: {
    question: '',
    components: [
      {
        component: 'V2AtomsSelection',
        props: {
          name: 'postal_is_same_as_residential',
          size: 'md',
          type: 'checkbox',
          inline: true,
          'no-box': true,
          'no-bg-selection-box': true,
          options: [
            {
              value: 'Yes',
              label: 'My postal address is the same as my residential address',
              name: 'postal-is-same-as-residential-yes'
            }
          ]
        }
      }
    ]
  },

  postStreetAddress: {
    question: 'Postal address',
    components: [
      {
        component: 'MoleculesFormAddress',
        props: {
          name: 'post_street_address',
          placeholder: 'Your postal address',
          defaultPostcode: '2000',
          defaultState: 'NSW',
          stickyAddress: true,
          showAddresslineError: false,
          'data-testid': 'postal-address-input',
          showPencilIcon: true
        }
      }
    ]
  },

  preferredContactMethod: {
    question: 'Preferred contact method for health fund',
    wrapperClass: 'mt-6',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'preferred_contact_method',
          label: 'Preferred contact method for health fund',
          theme: 'light',
          placeholder: 'Select method',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          dataArr: [
            { label: 'Email', value: 'Email' },
            { label: 'Post', value: 'Post' }
          ],
          'data-testid': 'preferred-contact-method-dropdown'
        }
      }
    ]
  },

  insurerMarketingConsent: {
    question: '',
    partitionLabel: 'Marketing preferences (optional)',
    components: [
      {
        component: 'V2AtomsSelection',
        props: {
          name: 'insurer_marketing_consent',
          size: 'md',
          type: 'checkbox',
          inline: true,
          'no-box': true,
          'no-bg-selection-box': true,
          'data-testid': 'insurer-marketing-consent-checkbox',
          options: getInsurerMarketingConsentOptions('HBF')
        }
      }
    ]
  },

  choosableExtras: {
    question: '',
    components: [
      {
        component: 'V2AtomsSelection',
        props: {
          name: 'choosable_extras',
          size: 'base',
          type: 'checkbox',
          cols: 2,
          mobileCols: 1,
          errorTooltip: true,
          checkValueError: false,
          options: [],
          'data-testid': 'choosable-extras-checkbox'
        }
      }
    ]
  },

  partnerTitle: {
    question: 'What is your partner\'s title',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'partner_title',
          wrapperClass: 'hidden lg:block',
          label: 'Title',
          theme: 'light',
          placeholder: 'Title',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          dataArr: PAGE_DATA.title,
          validator: 'emptyValidator',
          'data-testid': 'partner-title-dropdown'
        }
      },
      {
        component: 'MoleculesSelection',
        props: {
          name: 'partner_title',
          class: 'lg:hidden',
          type: 'radio',
          ticker: 'circle',
          options: PAGE_DATA.title,
          cols: 2,
          mobileCols: 2,
          'data-testid': 'partner-title-dropdown'
        }
      }
    ]
  },

  partnerFirstName: {
    question: 'First name',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'partner_first_name',
          placeholder: 'Enter first name',
          label: '',
          theme: 'light',
          'data-testid': 'partner-first-name-input'
        }
      }
    ]
  },

  partnerMiddleName: {
    question: 'Middle name <span class="font-normal">(optional)</span>',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'partner_middle_name',
          placeholder: 'Enter middle name',
          label: '',
          theme: 'light',
          'data-testid': 'partner-middle-name-input'
        }
      }
    ]
  },

  partnerLastName: {
    question: 'Last name',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'partner_last_name',
          placeholder: 'Enter last name',
          label: '',
          theme: 'light',
          'data-testid': 'partner-last-name-input'
        }
      }
    ]
  },

  partnerBirthdate: {
    question: 'Date of Birth',
    components: [
      {
        component: 'MoleculesBirthdate',
        props: {
          name: 'partner_dob',
          label: 'Date of birth',
          theme: 'light',
          placeholder: 'Enter your partner\'s date of birth (dd/mm/yyyy)',
          validAge: PAGE_DATA.validAge,
          disableDatepicker: true,
          'data-testid': 'partner-dob-input'
        }
      }
    ]
  },

  partnerGender: {
    question: 'What is your partner\'s gender',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'partner_gender',
          type: 'radio',
          ticker: 'circle',
          options: PAGE_DATA.gender,
          cols: 2,
          mobileCols: 2,
          'data-testid': 'partner-gender-radio'
        }
      }
    ]
  },

  partnerMobile: {
    question: 'Partner Mobile <span class="font-normal">(optional)</span>',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'partner_mobile',
          placeholder: 'Your partner\'s mobile number',
          label: '',
          theme: 'light',
          validator: 'mobileNumberValidator',
          'data-testid': 'partner-mobile-input'
        }
      }
    ]
  },

  partnerEmail: {
    question: 'Partner Email <span class="font-normal">(optional)</span>',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'partner_email',
          placeholder: 'Enter your partner\'s email',
          label: '',
          theme: 'light',
          validator: 'emailValidator',
          'data-testid': 'partner-email-input'
        }
      }
    ]
  },

  totalChildren: {
    question: 'How many children are covered in this policy?',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'total_children',
          label: 'Total Children',
          theme: 'light',
          placeholder: 'Total children',
          defaultValue: '0',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          dataArr: ['1','2','3','4','5','6','7','8','9','10'],
          'data-testid': 'total-children-dropdown'
        }
      }
    ]
  },

  children: {
    components: [
      {
        component: 'OrganismsHealthChildren',
        props: {
          name: 'children',
          formItems: CHILDREN_FORM,
          errorMap: CHILDREN_ERROR_MAP
        }
      }
    ]
  },

  income: {
    question: 'Your annual income',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'income',
          type: 'radio',
          ticker: 'circle',
          cols: 1,
          mobileCols: 1,
          options: PAGE_DATA.incomes.single,
          'data-testid': 'annual-income-radio'
        }
      }
    ]
  },

  govtDeclaration: {
    question: 'Government declaration',
    partition: true,
    partitionDesc: 'If, at any stage, you want to change tiers or stop receiving the Australian Government Rebate on Private Health Insurance as a reduced premium, you’ll need to speak to your new fund as soon as possible.',
    desc: 'I declare that the information in my Australian Government Rebate on Private Health Insurance is correct. I understand there are penalties for giving false or misleading information.',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'govt_declaration',
          type: 'radio',
          ticker: 'circle',
          options: ['Yes', 'No'],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'govt-declaration-radio'
        }
      }
    ]

  },
  isCovered: {
    question: 'Have you held hospital cover before?',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'is_covered',
          type: 'radio',
          ticker: 'circle',
          options: ['Yes', 'No'],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'held-hospital-cover-radio'
        }
      }
    ]
  },
  heldHospitalCoverContinuously: {
    question: 'Have you held hospital cover continuously since your 31st birthday?',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'held_hospital_cover_continuously',
          type: 'radio',
          ticker: 'circle',
          options: ['Yes', 'No'],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'held-hospital-cover-continuously-radio'
        }
      }
    ]
  },
  lhcAgePrimary: {
    question: 'How old were you when you first took out private hospital cover?',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'lhc_age_primary',
          placeholder: 'Age',
          label: '',
          type: 'number',
          theme: 'light',
          validator: 'age',
          validatorParam: [16, 100],
          wrapperClass: 'w-full lg:w-48',
          'data-testid': 'lhc-age-primary-input'
        }
      }
    ]
  },
  isCoveredPartner: {
    question: 'Has your partner held hospital cover before?',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'is_covered_partner',
          type: 'radio',
          ticker: 'circle',
          options: ['Yes', 'No'],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'partner-held-hospital-radio'
        }
      }
    ]
  },
  partnerHeldHospitalCoverContinuously: {
    question: 'Has your partner held hospital cover continuously since their 31st birthday?',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'partner_held_hospital_cover_continuously',
          type: 'radio',
          ticker: 'circle',
          options: ['Yes', 'No'],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'partner-held-hospital-cover-continuously-radio'
        }
      }
    ]
  },
  lhcAgePartner: {
    question: 'What was your partners age when you first took out private hospital cover?',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'lhc_age_partner',
          placeholder: 'Age',
          label: '',
          type: 'number',
          theme: 'light',
          wrapperClass: 'w-full lg:w-48',
          'data-testid': 'lhc-age-partner-input'
        }
      }
    ]
  },

  lhcMessage: {
    components: [
      {
        component: 'AtomsAlert',
        props: {
          size: 'p1',
          type: 'success',
          'full-width': true,
          'has-close': false,
          'data-testid': 'lhc-message-text'
        }
      }
    ]
  },

  insurer: {
    question: 'Fund Name',
    partitionLabel: 'Your previous fund',
    partition: true,
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'insurer',
          label: 'Previous Fund',
          theme: 'light',
          placeholder: 'Select your health fund',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          searchable: true,
          dataSrc: 'https://asset.compareclub.com.au/json/health-funnel-funds.json',
          'data-testid': 'previous-fund-primary-dropdown'
        }
      }
    ]
  },
  insurerOther: {
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'insurer_other',
          placeholder: 'Enter fund name',
          label: '',
          theme: 'light',
          'data-testid': 'previous-fund-other-primary-input'
        }
      }
    ]
  },
  membershipId: {
    question: 'Membership ID <span class="font-normal">(optional)</span>',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'membership_id',
          placeholder: 'Membership ID',
          label: '',
          theme: 'light',
          modelType: 'number',
          'data-testid': 'membership-id-primary-input'
        }
      }
    ]
  },
  partnerInsurer: {
    question: 'Fund Name',
    partitionLabel: 'Your partner\'s previous fund',
    partition: true,
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'partner_insurer',
          label: 'Previous Fund',
          theme: 'light',
          placeholder: 'Select your partner’s health fund',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          searchable: true,
          dataSrc: 'https://asset.compareclub.com.au/json/health-funnel-funds.json',
          'data-testid': 'partner-previous-fund-dropdown'
        }
      }
    ]
  },
  partnerInsurerOther: {
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'partner_insurer_other',
          placeholder: 'Enter fund name',
          label: '',
          theme: 'light',
          'data-testid': 'partner-previous-fund-other-input'
        }
      }
    ]
  },
  partnerMembershipId: {
    question: 'Membership ID <span class="font-normal">(optional)</span>',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'partner_membership_id',
          placeholder: 'Membership ID',
          label: '',
          theme: 'light',
          modelType: 'number',
          'data-testid': 'partner-membership-id-input'
        }
      }
    ]
  },
  hasMedicare: {
    question: 'Do you have a medicare card?',
    alertTitle: 'A valid Medicare card is required to switch your private health insurance.',
    alertDesc: 'If you\'re not an Australian citizen or permanent resident, you may need a different type of health cover based on your visa.',
    redirectPath: '/health-insurance/visitors-visa-ovhc/',
    redirectLabel: 'Explore cover options for visa holders',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'has_medicare',
          type: 'radio',
          ticker: 'circle',
          options: ['Yes', 'No'],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'has-medicare-radio'
        }
      }
    ]
  },
  medicareName: {
    question: 'Name on Medicare card',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'medicare_name',
          placeholder: 'Medicare name as on card',
          label: '',
          theme: 'light',
          validator: 'textValidator',
          'data-testid': 'medicare-name-input'
        }
      }
    ]
  },
  medicareFirstName: {
    wrapperClass: 'lg:col-start-1 lg:row-start-2',
    question: 'First name as on card',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'medicare_first_name',
          placeholder: 'Enter first name',
          label: '',
          theme: 'light',
          validator: 'textValidator',
          'data-testid': 'medicare-first-name-input'
        }
      }
    ]
  },
  medicareMiddleName: {
    wrapperClass: 'lg:col-start-1 lg:row-start-3',
    question: 'Middle initial <span class="font-normal">(optional)</span>',
    isToggle: true,
    toggleLabel: 'Add middle initial',
    toggleIcon: 'V2IconsPlus',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'medicare_middle_name',
          placeholder: 'Enter middle initial',
          label: '',
          theme: 'light',
          maxlength: '1',
          'data-testid': 'medicare-middle-initial-input'
        }
      }
    ]
  },
  medicareLastName: {
    wrapperClass: 'lg:col-start-2 lg:row-start-2',
    question: 'Last name',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'medicare_last_name',
          placeholder: 'Enter last name',
          label: '',
          theme: 'light',
          validator: 'textValidator',
          'data-testid': 'medicare-last-name-input'
        }
      }
    ]
  },
  medicareColor: {
    question: 'Card colour',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'medicare_color',
          label: 'Medicare card colour',
          theme: 'light',
          placeholder: 'Select card colour',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          dataArr: [
            { label: 'Green', value: 'Green' },
            { label: 'Blue', value: 'Blue' },
            { label: 'Yellow', value: 'Yellow' }
          ],
          'data-testid': 'medicare-color-dropdown'
        }
      }
    ]
  },
  medicareCardNumber: {
    wrapperClass: 'lg:col-start-1 lg:row-start-4',
    question: 'Medicare number',
    components: [
      {
        component: 'AtomsInput',
        desc: '10 digits, excluding the reference number.',
        props: {
          name: 'medicare_card_number',
          placeholder: 'Medicare card no.',
          label: '',
          theme: 'light',
          modelType: 'number',
          wrapperClass: 'w-full',
          maxlength: '10',
          'data-testid': 'medicare-number-input'
        }
      }
    ]
  },
  medicareExpired: {
    wrapperClass: 'lg:col-start-2 lg:row-start-4',
    question: 'Expiry date',
    components: [
      {
        component: 'AtomsDatepicker',
        props: {
          name: 'medicare_expired',
          placeholder: 'Exp (mm/yyyy)',
          format: 'MM/YYYY',
          valueType: 'MM/YYYY',
          theme: 'light',
          label: '',
          modelType: 'date',
          type: 'month',
          disableDatepicker: false,
          isMedicareExp: true,
          largeCalendar: true,
          'data-testid': 'medicare-expiry-input'
        }
      }
    ]
  },
  medicareCompliance: {
    wrapperClass: 'lg:col-span-2 lg:row-start-5',
    components: [
      {
        component: 'AtomsAlert'
      }
    ]
  },
  medicareEntitlement: {
    wrapperClass: 'lg:col-start-1 col-span-2',
    question: 'Everyone on the policy has full Medicare<br/>entitlements.',
    desc: 'Everyone on your policy must hold a valid Medicare card to qualify for the Australian Government Rebate on private health insurance as a reduced premium.',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'medicare_entitlement',
          type: 'radio',
          ticker: 'circle',
          options: ['Yes', 'No'],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'all-medicare-entitlement-radio'
        }
      }
    ]
  },
  paymentMethod: {
    question: 'Please select your payment method',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'payment_method',
          type: 'radio',
          ticker: 'circle',
          options: ['Direct debit', 'Credit card'],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'payment-method-radio'
        }
      }
    ]
  },

  bankAccName: {
    question: 'What is your Payment Account name?',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'bank_acc_name',
          placeholder: 'Your account name',
          label: '',
          theme: 'light',
          modelType: 'text-only',
          maxlength: `${ACCOUNT_NAME_MAX_LENGTH}`,
          'data-testid': 'payment-account-name-input'
        }
      }
    ]
  },

  bankName: {
    question: 'What is the name of your Financial institution?',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'financial_institution',
          placeholder: 'Your financial institution',
          label: '',
          theme: 'light',
          modelType: 'text-only',
          'data-testid': 'financial-institution-input'
        }
      }
    ]
  },

  bankAccount: {
    question: 'What is your BSB and Account Number?',
    class: 'grid lg:grid-cols-[11rem,1fr] gap-4 debit-cards-bsb-acc-details',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'bsb',
          placeholder: 'BSB',
          label: '',
          theme: 'light',
          modelType: 'number',
          'data-testid': 'bsb-number-input'
        }
      },
      {
        component: 'AtomsInput',
        props: {
          name: 'bank_no',
          placeholder: 'Account number',
          label: '',
          modelType: 'number',
          theme: 'light',
          'data-testid': 'bank-number-input'
        }
      }
    ]
  },

  nibCreditCard: {
    components: [
      {
        component: 'MoleculesPaymentGatewayNib'
      }
    ]
  },

  bupaCreditCard: {
    components: [
      {
        component: 'MoleculesPaymentGatewayStripe'
      }
    ]
  },

  hbfCreditCard: {
    components: [
      {
        component: 'MoleculesPaymentGatewayHbf'
      }
    ]
  },
  hifCreditCard: {
    components: [
      {
        component: 'MoleculesPaymentGatewayHif'
      }
    ]
  },

  cardNumber: {
    question: 'Card number',
    components: [
      {
        component: 'MoleculesPaymentGatewayTokenex',
        props: {
          name: 'card number',
          modelType: 'number',
          placeholder: 'Card number',
          theme: 'light'
        }
      }
    ]
  },

  cardType: {
    question: 'What is your Credit card type?',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'card_type',
          label: 'Your credit card type',
          placeholder: 'Your credit card type',
          theme: 'light',
          dataValue: 'value',
          disabled: true,
          wrapperClass: 'hidden lg:block',
          dataArr: ['VISA', 'Mastercard', 'American Express'],
          'data-testid': 'card-type-dropdown'
        }
      },
      {
        component: 'MoleculesSelection',
        props: {
          name: 'card_type',
          type: 'radio',
          ticker: 'circle',
          options: ['VISA', 'Mastercard', 'American Express'],
          disabled: true,
          class: 'lg:hidden',
          cols: 2,
          mobileCols: 2
        }
      }
    ]
  },

  cardExpDate: {
    question: 'What is your Credit card expiry date?',
    components: [
      {
        component: 'AtomsDatepicker',
        props: {
          name: 'card expiration date',
          placeholder: 'Exp (mm/yyyy)',
          format: 'mm/yyyy',
          valueType: 'mm/yyyy',
          dateFormat: 'mm/yyyy',
          theme: 'light',
          label: '',
          size: 'default',
          modelType: 'date',
          disableDatepicker: true,
          isMedicareExp: true,
          'data-testid': 'card-expiry-input'
        }
      }
    ]
  },

  cardholderName: {
    question: 'What is your Carholder\'s name?',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'cardholder_name',
          placeholder: 'Cardholder name',
          label: '',
          theme: 'light',
          'data-testid': 'card-holder-name-input'
        }
      }
    ]
  },

  paymentFrequency: {
    question: 'What is your preferred payment frequency?',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'payment_frequency',
          wrapperClass: 'hidden lg:block',
          label: 'Payment Frequency',
          placeholder: 'Payment Frequency',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          dataArr: ['Fortnightly', 'Monthly', 'Quarterly', 'HalfYearly', 'Annually'],
          'data-testid': 'payment-frequency-dropdown'
        }
      },
      {
        component: 'MoleculesSelection',
        props: {
          name: 'payment_frequency',
          class: 'lg:hidden',
          type: 'radio',
          ticker: 'circle',
          options: ['Fortnightly', 'Monthly', 'Quarterly', 'HalfYearly', 'Annually'],
          cols: 2,
          mobileCols: 2
        }
      }
    ]
  },

  policyStartDate: {
    question: 'What is your policy start date?',
    components: [
      {
        component: 'AtomsDatepicker',
        get desc () {
          // 2026-04-01 00:00 AEDT = 2026-03-31T13:00:00Z
          const showNotice = Date.now() < Date.UTC(2026, 2, 31, 13, 0, 0)
          return `<span class="text-sm">Your first payment will be calculated based on the start date and may differ from your ongoing cost.</span>${getPolicyStartEarliestDateNotice()}<br />${showNotice ? '<div class="border relative empty:hidden leading-[1.125rem] p-2 bg-semantic-states-warning-variant text-semantic-surface-on-surface border-semantic-states-warning-default rounded-lg w-full my-2 !text-sm !px-4">The updated 2026 price will apply to all payments made after the 1st of April.</div>' : ''}`
        },
        props: {
          name: 'start_date',
          label: 'Policy start date',
          placeholder: 'Policy start date (dd/mm/yyyy)',
          theme: 'light',

          disableDate: date => {
            // Determine max selectable date based on fund: HIF = 20 days, all others = 30 days
            const applicationStore = useHealthApplicationStore()
            const fund = applicationStore?.applicationData?.selected_product?.insurer?.toLowerCase?.()
              ?? applicationStore?.productDetails?.quote?.insurer?.name?.toLowerCase?.()
              ?? ''
            const maxDays = fund === 'hif' ? 20 : 30

            const maxDate = new Date()
            maxDate.setDate(maxDate.getDate() + maxDays)
            maxDate.setHours(23, 59, 59, 999)

            return date < getPolicyStartMinDate().toDate() || date > maxDate
          },
          'data-testid': 'policy-start-date-input'
        }
      }
    ]
  },

  sameAccount: {
    question: 'Would you like to claim refunds paid into the same account?',
    components: [
      {
        component: 'MoleculesSelection',
        props: {
          name: 'same_account',
          type: 'radio',
          ticker: 'circle',
          options: ['Yes', 'No'],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'claim-same-account-radio'
        }
      }
    ]
  },

  directCreditAccountName: {
    partition: true,
    partitionLabel: 'Nominated refund payment account',
    question: 'What is your Payment Account name?',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'account_name',
          placeholder: 'Your account name',
          label: '',
          theme: 'light',
          modelType: 'text-only',
          'data-testid': 'refund-account-name-input'
        }
      }
    ]
  },

  directCreditBankAccount: {
    question: 'What is your BSB and Account Number?',
    class: 'grid lg:grid-cols-[11rem,1fr] gap-4 debit-cards-bsb-acc-details',
    components: [
      {
        component: 'AtomsInput',
        props: {
          name: 'credit_bsb',
          placeholder: 'BSB',
          label: '',
          theme: 'light',
          modelType: 'number',
          'data-testid': 'refund-bsb-input'
        }
      },
      {
        component: 'AtomsInput',
        props: {
          name: 'credit_bank_no',
          placeholder: 'Account number',
          label: '',
          modelType: 'number',
          theme: 'light',
          'data-testid': 'refund-bank-number-input'
        }
      }
    ]
  },

  // placeholder component, no render
  acknowledgement: {
    wrapperClass: 'hidden',
    components: [
      {
        component: '',
        hidden: true
      }
    ]
  },

  review: {
    wrapperClass: 'hidden',
    components: [
      {
        component: '',
        hidden: true
      }
    ]
  }
}

export const FORM_HEADER = {
  [PERSONAL_DETAILS_STEP]: {
    title: `
      <strong class="font-bold">Personal details</strong>
    `,
    desc: '<p>We\'ve prefilled a few details from your quote. Please check they\'re correct.',
    component: 'AtomsHeadline',
    props: {
      class: '!leading-5'
    },
    buttons: {
      back: 'Back',
      main: 'Continue'
    }
  },

  [PARTNER_DETAILS_STEP]: {
    title: `
      <strong class="font-bold">Partner details</strong>
    `,
    desc: '<p>We\'ve prefilled a few details from your quote. Please check they\'re correct.</p>',
    component: 'AtomsHeadline',
    props: {
      class: '!leading-5'
    },
    buttons: {
      back: 'Back',
      main: 'Continue'
    }
  },

  [CHILDREN_DETAILS_STEP]: {
    title: `
      <strong class="font-bold">Dependant details</strong>
    `,
    desc: '<p>We\'ve prefilled a few details from your quote — please check they\'re correct.</p>',
    component: 'AtomsHeadline',
    props: {
      class: '!leading-5'
    },
    buttons: {
      back: 'Back',
      main: 'Continue'
    }
  },

  [REBATE_STEP]: {
    title: `
      <strong class="font-bold">Australian government rebate</strong>
    `,
    desc: '<p class="text-sm">Your Australian government rebate on private health insurance has already been applied to the prices shown.</p>',
    component: 'AtomsHeadline',
    props: {
      class: '!leading-5'
    },
    buttons: {
      back: 'Back',
      main: 'Continue'
    }
  },

  [LHC_STEP]: {
    title: `
      <strong class="font-bold">Lifetime health cover (LHC)</strong>
    `,
    desc: 'An LHC loading may apply if you first took out hospital cover after turning 31.',
    component: 'AtomsHeadline',
    props: {
      class: '!leading-5'
    },
    buttons: {
      back: 'Back',
      main: 'Continue'
    }
  },

  [CHOOSE_EXTRAS_STEP]: {
    title: `
      <strong class="font-bold">Choose your extras</strong>
    `,
    component: 'AtomsHeadline',
    props: {
      class: '!leading-5'
    },
    buttons: {
      back: 'Back',
      main: 'Continue'
    }
  },

  [ACKNOWLEDGEMENT_STEP]: {
    title: `
      <strong class="font-bold">Acknowledgements</strong>
    `,
    desc: '<p>Please review the information below and scroll to the end to agree.</p>',
    component: 'AtomsHeadline',
    props: {
      class: '!leading-5'
    },
    buttons: {
      back: 'Back',
      main: 'I acknowledge and agree'
    }
  },

  [REVIEW_STEP]: {
    title: `
      <strong class="font-bold">Review your application</strong>
    `,
    desc: '<p>Check everything below is correct before you purchase. Tap edit to make changes.</p>',
    component: 'AtomsHeadline',
    props: {
      class: 'hidden lg:block !leading-5'
    },
    buttons: {
      back: 'Back',
      main: 'Continue'
    }
  },

  [MEDICARE_STEP]: {
    title: `
      <strong class="font-bold">Medicare card details</strong>
    `,
    desc: '<p>Required to verify your eligibility and register your policy with your fund.</p>',
    component: 'AtomsHeadline',
    props: {
      class: '!leading-5'
    },
    buttons: {
      back: 'Back',
      main: 'Continue'
    }
  },

  [PAYMENT_STEP]: {
    title: `
      <strong class="font-bold">Payment details</strong>
    `,
    desc: '<p>Set up your payment and choose when your cover starts.</p>',
    component: 'AtomsHeadline',
    props: {
      class: '!leading-5'
    },
    buttons: {
      back: 'Back',
      main: 'Submit'
    }
  }

}

export const FORM_STEPS = [
  {
    label: 'Compare',
    icon: '/icons/application/sidebar/compare-card.svg',
    iconDimensions: {
      width: 24,
      height: 24
    }
  },
  {
    label: 'Your Details',
    icon: '/icons/application/sidebar/identity-card.svg',
    iconDimensions: {
      width: 24,
      height: 24
    }
  },
  {
    label: 'Rebate Details',
    icon: '/icons/application/sidebar/rebate-card.svg',
    iconDimensions: {
      width: 24,
      height: 24
    }
  },
  {
    label: 'LHC Loading',
    icon: '/icons/application/sidebar/loading-card.svg',
    iconDimensions: {
      width: 24,
      height: 24
    }
  },
  {
    label: 'Extras Selection',
    icon: '/icons/application/sidebar/heart-card.svg',
    iconDimensions: {
      width: 24,
      height: 24
    }
  },
  {
    label: 'Medicare Details',
    icon: '/icons/application/sidebar/medicare-card.svg',
    iconDimensions: {
      width: 24,
      height: 24
    }
  },
  {
    label: 'Terms',
    icon: '/icons/application/sidebar/medicare-card.svg',
    iconDimensions: {
      width: 24,
      height: 24
    }
  },
  {
    label: 'Review',
    icon: '/icons/application/sidebar/medicare-card.svg',
    iconDimensions: {
      width: 24,
      height: 24
    }
  },
  {
    label: 'Payment Details',
    icon: '/icons/application/sidebar/dollar-card.svg',
    iconDimensions: {
      width: 24,
      height: 24
    }
  },
  {
    label: 'Submit',
    icon: '/icons/application/sidebar/complete-card.svg',
    iconDimensions: {
      width: 17,
      height: 20
    }
  }
]

export const FORM_ITEMS_ARR = {
  [PERSONAL_DETAILS_STEP]: [
    'firstName',
    'middleName',
    'lastName',
    'birthdate',
    'mobile',
    'email',
    'title',
    'gender',
    'phone',
    'streetAddress',
    'postalIsSameAsResidential',
    'postStreetAddress',
    'preferredContactMethod',
    'insurerMarketingConsent'
  ],
  [PARTNER_DETAILS_STEP]: [
    'partnerTitle',
    'partnerFirstName',
    'partnerMiddleName',
    'partnerLastName',
    'partnerBirthdate',
    'partnerGender',
    'partnerMobile',
    'partnerEmail'
  ],
  [CHILDREN_DETAILS_STEP]: [
    'totalChildren',
    'children'
  ],
  [REBATE_STEP]: [
    'income',
    'govtDeclaration'
  ],
  [LHC_STEP]: [
    'isCovered',
    'isCoveredPartner',
    'heldHospitalCoverContinuously',
    'partnerHeldHospitalCoverContinuously',
    'lhcAgePrimary',
    'lhcAgePartner',
    'lhcMessage',
    'insurer',
    'insurerOther',
    'membershipId',
    'partnerInsurer',
    'partnerInsurerOther',
    'partnerMembershipId'
  ],
  [CHOOSE_EXTRAS_STEP]: [
    'choosableExtras'
  ],
  [MEDICARE_STEP]: [
    'hasMedicare',
    'medicareName',
    'medicareColor',
    'medicareCardNumber',
    'medicareExpired',
    'medicareCompliance',
    'medicareEntitlement'
  ],
  [ACKNOWLEDGEMENT_STEP]: [
    'acknowledgement'
  ],
  [REVIEW_STEP]: [
    'review'
  ],
  [PAYMENT_STEP]: [
    'paymentMethod',
    'paymentFrequency',
    'bankAccName',
    'bankName',
    'bankAccount',
    'policyStartDate',
    'sameAccount',
    'directCreditAccountName',
    'directCreditBankAccount',
    'nibCreditCard',
    'bupaCreditCard',
    'hbfCreditCard',
    'hifCreditCard',
    'cardholderName',
    'cardNumber',
    'cardExpDate',
    'cardType'
  ]
}

// --- Medicare card step redesign (HI-1288, gated by feature_HealthMedicareCardRedesign) ---

// Visual card-colour selector that replaces the `medicareColor` dropdown when the
// redesign flag is on. Values stay Green/Blue/Yellow (so the expiry-format flip and the
// Salesforce medicareColorToType mapping keep working) plus a new `None` option that
// routes to the OVHC funnel via the redirect fields below. The `image` on each option is
// the small swatch on the tile itself; the matching full card art lives in
// MEDICARE_CARD_IMAGES below.
export const MEDICARE_COLOR_REDESIGN = {
  question: 'Select a Medicare card colour type',
  // Grid placement inside the redesign 2-col Medicare-step layout (inert in the flag-off flex layout).
  wrapperClass: 'lg:col-span-2 lg:row-start-1',
  // OVHC redirect fields, rendered by the full-width alert when the 'None' tile is picked.
  alertTitle: 'A valid Medicare card is required to switch your private health insurance.',
  alertDesc: 'If you\'re not an Australian citizen or permanent resident, you may need a different type of health cover based on your visa.',
  redirectPath: '/health-insurance/visitors-visa-ovhc/',
  redirectLabel: 'Explore cover options for visa holders',
  components: [
    {
      // props mirror the life-stage selector in pages/health-insurance/quote/form/step0.vue:
      // ticker 'none' (no radio/checkbox indicator), image tiles, row direction.
      component: 'V2AtomsSelection',
      props: {
        name: 'medicare_color',
        type: 'radio',
        ticker: 'none',
        imagePosition: 'top',
        direction: 'row',
        size: 'small',
        cols: 4,
        mobileCols: 4,
        dataValue: 'value',
        returnFullObject: false,
        // 'None' is the no-Medicare-card signal — it mirrors answering 'No' to the flag-off
        // `hasMedicare` question: hides the remaining Medicare fields, blocks Continue and
        // surfaces the OVHC redirect alert built from the fields above.
        options: [
          { value: 'Green', label: 'Green', image: '/icons/application/medicare-card/green.svg', imageWidth: 32, imageHeight: 32 },
          { value: 'Blue', label: 'Blue', image: '/icons/application/medicare-card/blue.svg', imageWidth: 32, imageHeight: 32 },
          { value: 'Yellow', label: 'Yellow', image: '/icons/application/medicare-card/yellow.svg', imageWidth: 32, imageHeight: 32 },
          { value: 'None', label: 'None', image: '/icons/application/medicare-card/none.svg', imageWidth: 32, imageHeight: 32 }
        ],
        'data-testid': 'medicare-color-selection'
      }
    }
  ]
}

// The blank Medicare card art shown in the Medicare step header, one per card colour. The
// entered details are overlaid on top of it (see the #main template) — except for `None`,
// whose art already prints its own "NO CARD ON FILE" labels. Keys must match the option
// values above; an unrecognised/empty colour falls back to Green, like the expiry default.
export const MEDICARE_CARD_IMAGES = {
  Green: '/icons/application/medicare-card/medicare-card.png',
  Blue: '/icons/application/medicare-card/medicare-card-blue.png',
  Yellow: '/icons/application/medicare-card/medicare-card-yellow.png',
  None: '/icons/application/medicare-card/medicare-card-grey.png'
}

export const MEDICARE_ENTITLEMENT_REDESIGN = {
  wrapperClass: 'lg:col-span-2 lg:row-start-6',
  components: [
    {
      component: 'V2AtomsSelection',
      props: {
        name: 'medicare_entitlement',
        id: 'medicare-entitlement-checkbox',
        size: 'md',
        type: 'checkbox',
        inline: true,
        'no-box': true,
        'no-bg-selection-box': true,
        options: [
          {
            value: 'Yes',
            label: '<span class="font-normal">Everyone on this policy has full Medicare entitlements to qualify for the government rebate.</span>',
            'data-testid': 'medicare-entitlement-radio'
          }
        ],
        'data-testid': 'medicare-entitlement-checkbox'
      }
    }
  ]
}

// Medicare-step field order for the redesign: no `hasMedicare` Yes/No question (the `None`
// colour tile replaces it), split name, then card number / expiry / entitlement.
export const MEDICARE_ITEMS_REDESIGN = [
  'medicareColor',
  'medicareFirstName',
  'medicareMiddleName',
  'medicareLastName',
  'medicareCardNumber',
  'medicareExpired',
  'medicareCompliance',
  'medicareEntitlement'
]

// form re-order-helper
const reorderKeys = (obj, order) => {
  const slots = Object.keys(obj).filter(k => order.includes(k))
  return Object.fromEntries(
    Object.keys(obj).map(k => {
      const i = slots.indexOf(k)
      const key = i === -1 ? k : order[i]
      return [key, obj[key]]
    })
  )
}

//
const patchFields = (obj, patches) => {
  const flat = Object.fromEntries(
    Object.entries(patches).flatMap(([keys, patch]) =>
      keys.split(',').map(k => [k.trim(), patch])
    )
  )
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, flat[k] ? { ...v, ...flat[k] } : v])
  )
}

const CHILDREN_FORM_PATCHES = {
  childFirstName: {
    wrapperClass: 'form-sub-item child-first-name col-span-2 lg:col-span-1'
  },
  childLastName: {
    wrapperClass: 'form-sub-item child-last-name col-span-2 lg:col-span-1'
  },
  childMiddleName: {
    wrapperClass: 'form-sub-item child-middle-name col-start-1 col-span-2 lg:col-span-1',
    isToggle: true,
    toggleLabel: 'Add middle name',
    toggleIcon: 'V2IconsPlus'
  },
  childBirthDate: {
    question: 'Date of Birth',
    wrapperClass: 'col-span-2',
    components: [
      {
        component: 'MoleculesBirthdate',
        hidden: true,
        props: {
          label: '',
          placeholder: 'Enter your child\'s date of birth (dd/mm/yyyy)',
          validAge: [1, 120],
          disableDatepicker: true,
          theme: 'light',
          hideSuccessIcon: true,
          'data-testid': 'child-dob-input'
        }
      }
    ]
  },
  'isChildCovered,childCurrentFund,childGender,isChildStudent' : {
    wrapperClass: 'col-span-2'
  }
}

const REDESIGNED_CHILDREN_FORM_QUESTIONS = patchFields(CHILDREN_FORM, CHILDREN_FORM_PATCHES)

// REDESIGNED form questions + re-ordering keys
const PATCHES = {
  title: {
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'title',
          label: 'Title',
          theme: 'light',
          placeholder: 'Title',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          dataArr: PAGE_DATA.title,
          'data-testid': 'title-dropdown'
        }
      }
    ]
  },
  lastName: {
    wrapperClass: 'col-span-2 lg:col-span-1'
  },
  mobile: {
    wrapperClass: 'col-span-2 lg:col-span-1'
  },
  email: {
    wrapperClass: 'col-span-2 lg:col-span-1'
  },
  phone: {
    wrapperClass: 'col-start-1 col-span-2 lg:col-start-2 lg:col-span-1'
  },
  middleName: {
    wrapperClass: 'col-start-2',
    isToggle: true,
    toggleLabel: 'Add middle name',
    toggleIcon: 'V2IconsPlus'
  },
  birthdate: {
    wrapperClass: 'birthdate col-span-2 lg:col-span-1',
    components: [
      {
        component: 'MoleculesBirthdate',
        props: {
          label: '',
          placeholder: 'Enter your date of birth (dd/mm/yyyy)',
          validAge: [18,100],
          disableDatepicker: true,
          theme: 'light',
          hideSuccessIcon: true,
          'data-testid': 'birthdate-input'
        }
      }
    ]
  },
  gender: {
    wrapperClass: 'gender col-span-2'
  },
  streetAddress: {
    wrapperClass: 'col-span-2'
  },
  postalIsSameAsResidential: {
    wrapperClass: 'col-span-2'
  },
  postStreetAddress: {
    wrapperClass: 'col-span-2'
  },
  preferredContactMethod: {
    wrapperClass: 'col-span-2',
    components: [
      {
        component: 'V2AtomsSelection',
        props: {
          name: 'preferred_contact_method',
          type: 'radio',
          ticker: 'circle',
          options: [
            { label: 'Email', value: 'Email' },
            { label: 'Post', value: 'Post' }
          ],
          cols: 2,
          mobileCols: 2,
          'data-testid': 'preferred-contact-method-radio'
        }
      }
    ]
  },
  insurerMarketingConsent: {
    wrapperClass: 'col-span-2'
  },
  partnerTitle: {
    question: 'Title',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'partner_title',
          label: 'Title',
          theme: 'light',
          placeholder: 'Title',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          dataArr: PAGE_DATA.title,
          validator: 'emptyValidator',
          'data-testid': 'partner-title-dropdown'
        }
      }
    ]
  },
  partnerMiddleName: {
    wrapperClass: 'col-start-2',
    isToggle: true,
    toggleLabel: 'Add middle name',
    toggleIcon: 'V2IconsPlus'
  },
  partnerLastName: {
    wrapperClass: 'col-span-2 lg:col-span-1'
  },
  partnerBirthdate: {
    wrapperClass: 'birthdate col-span-2 lg:col-span-1',
    components: [
      {
        component: 'MoleculesBirthdate',
        props: {
          name: 'partner_dob',
          label: '',
          theme: 'light',
          placeholder: 'Enter your partner\'s date of birth (dd/mm/yyyy)',
          validAge: PAGE_DATA.validAge,
          disableDatepicker: true,
          'data-testid': 'partner-dob-input'
        }
      }
    ]
  },
  partnerGender: {
    question: 'Gender',
    wrapperClass: 'gender col-span-2'
  },
  partnerMobile: {
    wrapperClass: 'col-span-2 lg:col-span-1'
  },
  partnerEmail: {
    wrapperClass: 'col-span-2 lg:col-span-1'
  },
  totalChildren: {
    hidden: true
  },
  children: {
    components: [
      {
        component: 'OrganismsHealthChildren',
        props: {
          name: 'children',
          redesigned: true,
          formItems: REDESIGNED_CHILDREN_FORM_QUESTIONS,
          errorMap: CHILDREN_ERROR_MAP
        }
      }
    ]
  },
  income: {
    question: 'Annual income',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'income',
          label: 'Annual income',
          theme: 'light',
          placeholder: 'Annual income',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          dataArr: PAGE_DATA.incomes.single,
          'data-testid': 'annual-income-dropdown'
        }
      }
    ]
  },
  govtDeclaration: {
    question: '',
    partition: false,
    partitionDesc: '',
    desc: '',
    components: [
      {
        component: 'V2AtomsSelection',
        props: {
          name: 'govt_declaration',
          size: 'md',
          type: 'checkbox',
          inline: true,
          'no-box': true,
          'no-bg-selection-box': true,
          tickerPosition: 'top',
          options: [
            {
              value: 'Yes',
              label: '<span class="font-extrabold">I declare that the information I have provided is complete and correct.</span> <span class="font-normal">I understand that giving false or misleading information is a serious offence.</span>',
              'data-testid': 'govt-declaration-radio'
            }
          ]
        }
      }
    ]
  },
  bankName: { question: 'Financial institution' },
  bankAccName: { question: 'Account name' },
  bankAccount: {
    question: 'Bank account',
    wrapperClass: 'col-span-2'
  },
  paymentMethod: {
    question: 'Payment method',
    wrapperClass: 'col-span-2'
  },
  paymentFrequency: {
    question: 'Payment frequency',
    wrapperClass: 'col-span-2',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'payment_frequency',
          label: 'Payment Frequency',
          placeholder: 'Payment Frequency',
          dataValue: 'value',
          dataInputDisplay: 'label',
          dataListDisplay: 'label',
          dataArr: ['Fortnightly', 'Monthly', 'Quarterly', 'HalfYearly', 'Annually'],
          'data-testid': 'payment-frequency-dropdown'
        }
      }
    ]
  },
  cardholderName: {
    question: 'Cardholder name as on card',
    wrapperClass: 'col-span-2 lg:col-span-1'
  },
  cardType: {
    question: 'Card type',
    components: [
      {
        component: 'MoleculesSelectDropdown',
        props: {
          name: 'card_type',
          label: 'Your credit card type',
          placeholder: 'Your credit card type',
          theme: 'light',
          dataValue: 'value',
          disabled: true,
          dataArr: ['VISA', 'Mastercard', 'American Express'],
          'data-testid': 'card-type-dropdown'
        }
      }
    ]
  },
  cardExpDate: {
    question: 'Exp date'
  },
  cardNumber: {
    question: 'Card number',
    wrapperClass: 'col-span-2 lg:col-span-1'
  },
  'hbfCreditCard,hifCreditCard,bupaCreditCard,nibCreditCard,sameAccount': {
    wrapperClass: 'col-span-2'
  },
  policyStartDate: {
    question: 'Policy start date',
    wrapperClass: 'col-span-2',
    components: [
      Object.defineProperties({}, {
        ...Object.getOwnPropertyDescriptors(FORM_QUESTIONS.policyStartDate.components[0]),
        props: {
          value: {
            ...FORM_QUESTIONS.policyStartDate.components[0].props,
            label: ''
          },
          enumerable: true,
          writable: true,
          configurable: true
        }
      })
    ]
  }
}

export const REDESIGNED_FORM_QUESTIONS = reorderKeys(
  patchFields(FORM_QUESTIONS, PATCHES),
  ['email', 'mobile', 'phone', 'cardholderName','cardNumber','cardType']
)

export const ERROR_MAP_REDESIGNED = {
  ...ERROR_MAP,
  mobile: 'Enter a 10-digit mobile number starting with 04',
  partner_mobile: 'Enter a 10-digit mobile number starting with 04',
  phone: 'Enter a 10-digit landline number',
  birthdate: 'Enter your date of birth in DD/MM/YYYY format',
  partner_birthdate: 'Enter date of birth in DD/MM/YYYY format', 
  govt_declaration: 'Please confirm the declaration',
  medicare_card_number: 'Enter your Medicare card number',
  medicare_expired: 'Enter a valid expiry date',

  bsb: 'Enter your BSB',
  bank_no: 'Enter your account number',
  bank_acc_name: 'Enter your account name',
  bank_name: 'Enter your financial institution',

  credit_bsb: 'Enter your credit BSB',
  credit_bank_no: 'Enter your credit account number',

  card_number: 'Enter valid credit card',
  hbf_credit_card: 'Enter valid credit card'
}

export const BRAZE_APPLICATION_START = 'health_online_application_start'

export const BRAZE_APPLICATION_PROGRESS = 'health_online_application_progress'

export const BRAZE_APPLICATION_COMPLETE = 'health_online_application_complete'